import React, { MouseEventHandler, ReactElement, useState } from "react";
import { PageContainer } from '@amzn/stencil-react-components/page';
import { LinkCard } from '@amzn/stencil-react-components/card';
import { Col, Row, Spacer, View } from "@amzn/stencil-react-components/layout";
import { Text } from '@amzn/stencil-react-components/text';
import { PathNames } from "src/constant/constants";
import { Redirect } from "react-router-dom";
import { Link } from "@amzn/stencil-react-components/link";

const STRINGS_DISPLAYED = {
  create_event_model_heading: "Create a new Event Model",
  create_event_model_description: "Onboarding a new event model in EASE includes defining the rules and solutions that EASE needs to execute.",
  manage_event_model_heading: "Manage your existing Event Models",
  replay_failed_events_heading: "Audit or Replay events",
  replay_failed_events_description: "Audit and Replay failed events for your trigger",
  manage_event_model_description: "Manage your existing event models, that you have access to.",
  what_is_ease_heading: "What is EASE?",
  ease_description: "EASE is an IFTTT (“if this, then that”) solution that connects apps across PXT to enable diverse set of applications. With EASE, clients don’t need infrastructure setup, and downstream/upstream onboarding. Tenant teams are expected to define integration condition as a configuration and rest is taken care by EASE.",
  ease_authoring_portal_description: "Through the EASE Authoring portal, you will be able to define your event models by which you will be able to launch your IFTTT journeys through EASE.",
  refer_steps: "Refer the steps below to define your events.",
  step_1_heading: "Step 1: Select Trigger",
  step_2_heading: "Step 2: Define Rule",
  step_3_heading: "Step 3: Select Solution",
  step_1_description: "In the first step, you choose the event trigger from a preconfigured list. The EASE trigger serves as the ingress condition, initiating the execution of the entire event model.",
  step_2_description: "In the second step, you will outline the logical conditions necessary for the system to execute solutions associated with the event. Event rules are expressed as JsonLogic statements, supporting a range of logical, boolean, numeric, various array and string operations. During the rule-building process, you will have access to employee-level pre-defined attributes obtained from downstream systems like PAPI, TAAPI, DLS, etc.",
  step_3_description: "In the final step, you will select the action/solution that you want to execute. You will give the required details for the solution that EASE will execute for your event.<br/>Showing a Tile on AtoZ, an MRI on My HR, Creating a Case are a few Solutions available in EASE.",
  step_1_link: "Click here to see the list of supported triggers.",
  step_2_link: "Click here to see the list of supported attributes.",
  step_3_link: "Click here to see the list of supported solutions.",
}

/**
 * Home page in EASE Self Service UI portal.
 */
export const HomePage = () => {
  const [redirectToUrl, setRedirectToUrl] = useState<string>("");

  return redirectToUrl
    ? <Redirect
      push={true}
      to={{
        pathname: redirectToUrl
      }}
    />
    : <PageContainer backgroundColor={"white"}>
        <Col gridGap={"0px"}>
          <Row gridGap={"28px"}>
            <NavigationCard
              headingText={STRINGS_DISPLAYED.create_event_model_heading}
              descriptionText={STRINGS_DISPLAYED.create_event_model_description}
              onClick={(_) => {
                setRedirectToUrl(PathNames.CREATE_EVENT_MODELS_PAGE);
              }}
            />

            <NavigationCard
              headingText={STRINGS_DISPLAYED.manage_event_model_heading}
              descriptionText={STRINGS_DISPLAYED.manage_event_model_description}
              onClick={(_) => {
                setRedirectToUrl(PathNames.MANAGE_EVENT_MODELS_PAGE);
              }}
            />

            <NavigationCard
              headingText={STRINGS_DISPLAYED.replay_failed_events_heading}
              descriptionText={STRINGS_DISPLAYED.replay_failed_events_description}
              onClick={(_) => {
                setRedirectToUrl(PathNames.REPLAY_FAILED_EVENTS_PAGE);
              }}
            />
          </Row>
          <Spacer height={"55px"} />

          <Col 
            backgroundColor={"#379AEC"}
            alignItems={"center"}
            padding={{left: "20%", right: "20%", top: "18px", bottom: "18px"}}
            justifyContent={"center"}
            gridGap={"16px"}
          >
            <Text color={"white"} fontSize={"25px"} lineHeight={"30px"}>{STRINGS_DISPLAYED.what_is_ease_heading}</Text>
            <Text color={"white"} fontSize={"18px"} lineHeight={"30px"}>
              {STRINGS_DISPLAYED.ease_description}
              <br />
              {STRINGS_DISPLAYED.ease_authoring_portal_description}
              <br />
              {STRINGS_DISPLAYED.refer_steps}
            </Text>
          </Col>

          <Row flex={9}>
            <EASEStepDescriptionComponent 
              heading={STRINGS_DISPLAYED.step_1_heading}
              description={STRINGS_DISPLAYED.step_1_description}
              linkText={STRINGS_DISPLAYED.step_1_link}
              link={"#"}
              backgroundColor={"#AFD7F7"}
            />

            <EASEStepDescriptionComponent 
              heading={STRINGS_DISPLAYED.step_2_heading}
              description={STRINGS_DISPLAYED.step_2_description}
              linkText={STRINGS_DISPLAYED.step_2_link}
              link={"#"}
              backgroundColor={"#87C2F4"}
            />

            <EASEStepDescriptionComponent 
              heading={STRINGS_DISPLAYED.step_3_heading}
              description={STRINGS_DISPLAYED.step_3_description}
              linkText={STRINGS_DISPLAYED.step_3_link}
              link={"#"}
              backgroundColor={"#5FAEF0"}
            />
          </Row>
        </Col>
      </PageContainer>
}

/**
 * Renders cards on the page to let the users navigate to different pages in EASE Authoring portal.
 */
interface NavigationCardProps {
  headingText: string;
  descriptionText: string;
  onClick: MouseEventHandler;
}
const NavigationCard: React.FC<NavigationCardProps> = ({
  headingText, descriptionText, onClick,
}): ReactElement => {
  return <LinkCard
    width={"50%"}
    justifyContent={"start"}
    alignItems={"start"}
    onClick={onClick}
  >
    <Col gridGap={"16px"}>
      <Text fontSize={"25px"}>
        {headingText}
      </Text>
      <Text color={"neutral70"}>
        {descriptionText}
      </Text>
    </Col>
  </LinkCard>
}

/**
 * Renders components to describe the steps by which users need to onboard their IFTTT journeys on EASE.
 */
interface EASEStepDescriptionComponentProps {
  heading: string;
  description: string;
  linkText: string;
  link: string;
  backgroundColor: string;
}
const EASEStepDescriptionComponent = 
  ({heading, description, linkText, link, backgroundColor}: EASEStepDescriptionComponentProps) => {
  
  return <Col 
    backgroundColor={backgroundColor}
    flex={3}
    alignItems={"center"}
    justifyContent={"center"}
    padding={{top: "16px", bottom: "16px", right: "5%", left: "5%"}}
    gridGap={"12px"}
  >
    <Text color={"white"} fontSize={"22px"}>
      {heading}
    </Text>
    <Text color={"white"} textAlign={"center"}>
      {description}
    </Text>
    <Link href={link} color={"white"} textAlign={"center"}>
      {linkText}
    </Link>
  </Col>
}
